import { Fragment, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import type { LucideIcon } from "lucide-react";
import {
  ArrowRight,
  CalendarClock,
  Check,
  ChevronDown,
  ClipboardCopy,
  Folder,
  FolderUp,
  Gauge,
  Globe,
  KeyRound,
  Layers,
  LifeBuoy,
  MapPin,
  MonitorUp,
  Power,
  RotateCcw,
  Server,
  Settings2,
  Timer,
  UploadCloud,
  Zap,
} from "lucide-react";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { FTP_START_URL, ON_DEMAND_FTP_PATH, useOnDemandFtpCopy } from "@/content/onDemandFtp";
import { UPLOADER_URL, WEB_UPLOADER_PATH } from "@/content/webUploader";
import panel1x from "@/assets/on-demand-ftp/ftp-panel-1x.webp";
import panel2x from "@/assets/on-demand-ftp/ftp-panel-2x.webp";

// "How on-demand FTP works" at photohawk.com/on-demand-ftp: public and indexable, our own help page for the
// on-demand FTP servers (it replaces the unpublished help-centre article). Linked from the "Your FTP server" panel,
// /web-uploader, the pricing pages, the footer and the shared-FTP reminder emails. It opens with "the web uploader
// is faster" (the standard way to upload); FTP is for when it's essential. Copy: src/content/onDemandFtp/copy.<lang>.ts.
// The screenshot is the real panel with demo data (alex@example.com, swift-heron-4821.ftp.photohawk.com).

const CANONICAL = `https://photohawk.com${ON_DEMAND_FTP_PATH}`;
/** Its link-preview image (scripts/brand-assets/generate.sh). RouteMeta renders the full og:image tags. */
const OG_IMAGE = "https://photohawk.com/images/og/on-demand-ftp.jpg";

const PANEL_SHOT = { src: panel1x, srcSet: `${panel1x} 1x, ${panel2x} 2x`, width: 512, height: 584 };

const BEFORE_ICONS: Record<string, LucideIcon> = { parallel: Layers, adaptive: Gauge, live: Globe, resume: RotateCcw };
const STEP_ICONS: Record<string, LucideIcon> = { open: MonitorUp, start: Power, connect: ClipboardCopy, upload: FolderUp };
const TROUBLE_ICONS: Record<string, LucideIcon> = { connect: Server, listing: Folder, logins: KeyRound };

/** Copy markup: «Label» → bold (a button or setting as the app shows it), `text` → monospace (an address or path). */
const Rich = ({ text }: { text: string }) => (
  <>
    {text.split(/(«[^»]+»|`[^`]+`)/).map((part, i) => {
      if (part.startsWith("«") && part.endsWith("»")) return <strong key={i} className="font-semibold text-foreground">{part.slice(1, -1)}</strong>;
      if (part.startsWith("`") && part.endsWith("`"))
        return <code key={i} className="rounded bg-muted px-1 py-0.5 font-mono [overflow-wrap:anywhere] text-[0.9em] text-foreground">{part.slice(1, -1)}</code>;
      return <Fragment key={i}>{part}</Fragment>;
    })}
  </>
);

const IconTile = ({ icon: Icon, className = "" }: { icon: LucideIcon; className?: string }) => (
  <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-hawk-orange/10 ${className}`}>
    <Icon size={18} className="text-hawk-orange" aria-hidden="true" />
  </div>
);

const Card = ({ icon, title, children, id }: { icon: LucideIcon; title: string; children: ReactNode; id: string }) => (
  <section className="rounded-2xl border border-border bg-card p-6 shadow-sm" aria-labelledby={id}>
    <div className="mb-3 flex items-center gap-3">
      <IconTile icon={icon} />
      <h2 id={id} className="font-display text-xl font-bold md:text-2xl">{title}</h2>
    </div>
    {children}
  </section>
);

const Ticks = ({ items }: { items: { id: string; text: string }[] }) => (
  <ul className="space-y-2.5">
    {items.map((p) => (
      <li key={p.id} className="flex gap-2.5 text-muted-foreground">
        <Check size={18} className="mt-0.5 flex-shrink-0 text-hawk-orange" aria-hidden="true" />
        <span><Rich text={p.text} /></span>
      </li>
    ))}
  </ul>
);

const OnDemandFtp = () => {
  const { copy } = useOnDemandFtpCopy();
  const { hero, before, what, steps, panel, details, password, folders, off, regions, filezilla, trouble, faq, cta } = copy;

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{copy.seo.title}</title>
        <meta name="description" content={copy.seo.description} />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={CANONICAL} />
        <meta property="og:title" content={copy.seo.title} />
        <meta property="og:description" content={copy.seo.description} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta name="twitter:title" content={copy.seo.title} />
        <meta name="twitter:description" content={copy.seo.description} />
        <meta name="twitter:image" content={OG_IMAGE} />
      </Helmet>
      <AppHeader variant="marketing" />

      <main>
        {/* Hero, and straight away: the web uploader is faster (the standard way to upload) */}
        <section className="relative overflow-hidden bg-hawk-navy pb-16 pt-32 text-white md:pb-20">
          <div className="absolute inset-0 bg-radial-orange opacity-20" />
          <div className="container relative z-10 mx-auto px-4">
            <div className="mx-auto max-w-3xl text-center">
              <div className="mb-5 flex flex-wrap items-center justify-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-hawk-orange to-hawk-gold px-3 py-1 text-sm font-bold uppercase tracking-wide text-white shadow-glow">
                  <Server size={14} aria-hidden="true" />
                  {hero.badge}
                </span>
                <span className="text-sm font-medium text-white/80">{hero.eyebrow}</span>
              </div>
              <h1 className="mb-5 text-4xl font-display font-bold md:text-5xl">{hero.title}</h1>
              <p className="mx-auto max-w-2xl text-lg text-white/80 md:text-xl">{hero.subtitle}</p>
            </div>

            <aside
              className="mx-auto mt-10 max-w-3xl rounded-2xl border-2 border-hawk-gold/70 bg-card p-6 text-left text-foreground shadow-2xl md:p-8"
              aria-labelledby="before-heading"
            >
              <div className="mb-2 flex items-center gap-3">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-hawk-orange to-hawk-gold">
                  <Zap size={18} className="text-white" aria-hidden="true" />
                </div>
                <h2 id="before-heading" className="font-display text-xl font-bold md:text-2xl">{before.title}</h2>
              </div>
              <p className="mb-4 text-muted-foreground">{before.body}</p>
              <ul className="mb-6 grid gap-3 sm:grid-cols-2">
                {before.points.map((p) => {
                  const Icon = BEFORE_ICONS[p.id] ?? Check;
                  return (
                    <li key={p.id} className="flex gap-3 text-sm">
                      <Icon size={18} className="mt-0.5 flex-shrink-0 text-hawk-orange" aria-hidden="true" />
                      <span>{p.text}</span>
                    </li>
                  );
                })}
              </ul>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-5">
                <Button variant="hero" size="lg" asChild>
                  <a href={UPLOADER_URL}>
                    {before.button}
                    <ArrowRight />
                  </a>
                </Button>
                <Link to={WEB_UPLOADER_PATH} className="inline-flex items-center gap-1.5 font-semibold text-hawk-orange hover:underline">
                  {before.link}
                  <ArrowRight size={16} aria-hidden="true" />
                </Link>
              </div>
            </aside>
          </div>
        </section>

        {/* What it is, and the shared FTP closing */}
        <section className="py-16 md:py-20" aria-labelledby="what-heading">
          <div className="container mx-auto max-w-3xl px-4">
            <h2 id="what-heading" className="mb-3 text-3xl font-display font-bold md:text-4xl">{what.heading}</h2>
            <p className="mb-6 text-lg text-muted-foreground">{what.body}</p>
            <p className="flex flex-col gap-3 rounded-xl border border-hawk-gold/50 bg-hawk-orange/5 p-4 sm:flex-row sm:items-center">
              <span className="inline-flex flex-shrink-0 items-center gap-2 self-start rounded-full bg-hawk-orange/10 px-3 py-1 text-sm font-semibold text-hawk-orange sm:self-auto">
                <CalendarClock size={14} aria-hidden="true" />
                {what.date}
              </span>
              <span className="text-sm">{what.closing}</span>
            </p>
          </div>
        </section>

        {/* Four steps */}
        <section id="how" className="scroll-mt-24 bg-secondary/30 py-16 md:py-20" aria-labelledby="steps-heading">
          <div className="container mx-auto max-w-6xl px-4">
            <div className="mx-auto mb-10 max-w-2xl text-center">
              <h2 id="steps-heading" className="mb-3 text-3xl font-display font-bold md:text-4xl">{steps.heading}</h2>
              <p className="text-muted-foreground">{steps.subtitle}</p>
            </div>
            <ol className="grid gap-4 md:grid-cols-4 md:gap-6">
              {steps.items.map((s, i) => {
                const Icon = STEP_ICONS[s.id] ?? Check;
                return (
                  <li key={s.id} className="relative flex gap-4 rounded-2xl border border-border bg-card p-5 shadow-sm md:flex-col md:gap-3">
                    <div className="relative flex-shrink-0">
                      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-hawk-orange to-hawk-gold">
                        <Icon size={22} className="text-white" aria-hidden="true" />
                      </div>
                      <span className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-hawk-navy text-xs font-bold text-white" aria-hidden="true">
                        {i + 1}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <h3 className="mb-1 font-semibold"><span className="sr-only">{i + 1}. </span>{s.title}</h3>
                      <p className="text-sm text-muted-foreground"><Rich text={s.body} /></p>
                    </div>
                    {i < steps.items.length - 1 && (
                      <ArrowRight
                        size={20}
                        className="absolute -right-[19px] top-11 z-10 hidden text-hawk-orange md:block"
                        aria-hidden="true"
                      />
                    )}
                  </li>
                );
              })}
            </ol>
            <div className="mt-10 flex flex-col items-center gap-2 text-center">
              <Button variant="default" size="lg" asChild>
                <a href={FTP_START_URL}>
                  <Server />
                  {steps.button}
                </a>
              </Button>
              <p className="text-sm text-muted-foreground">{steps.buttonNote}</p>
            </div>
          </div>
        </section>

        {/* The panel: a real screenshot */}
        <section className="py-16 md:py-20" aria-labelledby="panel-heading">
          <div className="container mx-auto grid max-w-5xl items-center gap-10 px-4 md:grid-cols-2 md:gap-14">
            <div>
              <h2 id="panel-heading" className="mb-3 text-3xl font-display font-bold md:text-4xl">{panel.heading}</h2>
              <p className="mb-5 text-muted-foreground"><Rich text={panel.body} /></p>
              <Ticks items={panel.points} />
            </div>
            <div className="flex justify-center">
              <img
                src={PANEL_SHOT.src}
                srcSet={PANEL_SHOT.srcSet}
                width={PANEL_SHOT.width}
                height={PANEL_SHOT.height}
                alt={panel.alt}
                loading="lazy"
                decoding="async"
                className="block h-auto w-full max-w-[440px] rounded-xl border border-border bg-white shadow-xl"
              />
            </div>
          </div>
        </section>

        {/* Connection details */}
        <section className="bg-secondary/30 py-16 md:py-20" aria-labelledby="details-heading">
          <div className="container mx-auto max-w-3xl px-4">
            <h2 id="details-heading" className="mb-2 text-3xl font-display font-bold md:text-4xl">{details.heading}</h2>
            <p className="mb-6 text-muted-foreground">{details.subtitle}</p>
            <dl className="divide-y divide-border overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
              {details.rows.map((r) => (
                <div key={r.id} className="grid gap-1 px-5 py-3.5 sm:grid-cols-[11rem_1fr] sm:gap-4">
                  <dt className="text-sm font-semibold">{r.label}</dt>
                  <dd className="min-w-0 text-sm text-muted-foreground"><Rich text={r.value} /></dd>
                </div>
              ))}
            </dl>
            <p className="mt-5 flex gap-3 rounded-xl border border-hawk-gold/50 bg-card p-4 text-sm">
              <RotateCcw size={18} className="mt-0.5 flex-shrink-0 text-hawk-orange" aria-hidden="true" />
              <span>{details.switching}</span>
            </p>
          </div>
        </section>

        {/* Password, folders, switching off, regions */}
        <section className="py-16 md:py-20">
          <div className="container mx-auto grid max-w-5xl gap-6 px-4 md:grid-cols-2">
            <Card id="password-heading" icon={KeyRound} title={password.heading}>
              <p className="text-muted-foreground"><Rich text={password.body} /></p>
            </Card>
            <Card id="folders-heading" icon={Folder} title={folders.heading}>
              <p className="mb-3 text-muted-foreground">{folders.body}</p>
              <p className="mb-4 overflow-x-auto rounded-lg bg-hawk-navy px-4 py-3 font-mono text-sm text-white">{folders.path}</p>
              <Ticks items={folders.points} />
            </Card>
            <Card id="off-heading" icon={Timer} title={off.heading}>
              <p className="mb-4 text-muted-foreground">{off.body}</p>
              <Ticks items={off.points} />
            </Card>
            <Card id="regions-heading" icon={MapPin} title={regions.heading}>
              <p className="text-muted-foreground">{regions.body}</p>
            </Card>
          </div>
        </section>

        {/* FileZilla example */}
        <section className="bg-secondary/30 py-16 md:py-20" aria-labelledby="filezilla-heading">
          <div className="container mx-auto max-w-3xl px-4">
            <div className="mb-6 flex items-center gap-3">
              <IconTile icon={Settings2} />
              <h2 id="filezilla-heading" className="text-3xl font-display font-bold md:text-4xl">{filezilla.heading}</h2>
            </div>
            <p className="mb-6 text-muted-foreground">{filezilla.subtitle}</p>
            <ol className="space-y-3">
              {filezilla.steps.map((s, i) => (
                <li key={s.id} className="flex gap-4 rounded-xl border border-border bg-card p-4">
                  <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-hawk-orange text-sm font-bold text-white" aria-hidden="true">
                    {i + 1}
                  </span>
                  <span className="min-w-0 text-muted-foreground"><Rich text={s.text} /></span>
                </li>
              ))}
            </ol>
            <p className="mt-6 flex gap-3 rounded-xl border border-hawk-gold/50 bg-card p-4 text-sm">
              <Zap size={18} className="mt-0.5 flex-shrink-0 text-hawk-orange" aria-hidden="true" />
              <span><Rich text={filezilla.tip} /></span>
            </p>
          </div>
        </section>

        {/* Troubleshooting */}
        <section className="py-16 md:py-20" aria-labelledby="trouble-heading">
          <div className="container mx-auto max-w-5xl px-4">
            <div className="mb-8 flex items-center gap-3">
              <IconTile icon={LifeBuoy} />
              <h2 id="trouble-heading" className="text-3xl font-display font-bold md:text-4xl">{trouble.heading}</h2>
            </div>
            <ul className="grid gap-4 md:grid-cols-3">
              {trouble.items.map((t) => {
                const Icon = TROUBLE_ICONS[t.id] ?? Check;
                return (
                  <li key={t.id} className="flex gap-4 rounded-2xl border border-border bg-card p-5">
                    <IconTile icon={Icon} />
                    <div className="min-w-0">
                      <h3 className="mb-1 font-semibold">{t.title}</h3>
                      <p className="text-sm text-muted-foreground"><Rich text={t.body} /></p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>

        {/* FAQ: native <details>, so every answer is in the server-rendered HTML */}
        <section className="bg-secondary/30 py-16 md:py-20" aria-labelledby="faq-heading">
          <div className="container mx-auto max-w-3xl px-4">
            <h2 id="faq-heading" className="mb-8 text-center text-3xl font-display font-bold md:text-4xl">{faq.heading}</h2>
            <div className="divide-y divide-border rounded-2xl bg-card px-6 shadow-lg">
              {faq.items.map((item) => (
                <details key={item.q} className="group py-1">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-4 text-left text-base font-medium hover:underline [&::-webkit-details-marker]:hidden">
                    {item.q}
                    <ChevronDown size={16} className="flex-shrink-0 transition-transform duration-200 group-open:rotate-180" aria-hidden="true" />
                  </summary>
                  <p className="pb-4 text-muted-foreground"><Rich text={item.a} /></p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA: the web uploader first */}
        <section className="relative overflow-hidden bg-hawk-navy py-20 text-white">
          <div className="absolute inset-0 bg-radial-orange opacity-20" />
          <div className="container relative z-10 mx-auto max-w-3xl px-4 text-center">
            <UploadCloud className="mx-auto mb-4 text-hawk-orange" size={32} aria-hidden="true" />
            <h2 className="mb-3 text-3xl font-display font-bold md:text-4xl">{cta.heading}</h2>
            <p className="mb-8 text-white/80">{cta.body}</p>
            <div className="flex flex-wrap justify-center gap-3">
              <Button variant="hero" size="lg" asChild>
                <a href={UPLOADER_URL}>
                  {cta.primary}
                  <ArrowRight />
                </a>
              </Button>
              <Button variant="heroOutline" size="lg" asChild>
                <a href={FTP_START_URL}>
                  <Server />
                  {cta.secondary}
                </a>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default OnDemandFtp;
